import styled from "styled-components";

// Paleta escura FIXA (não segue o tema do app): é uma tela de foto — o que
// importa é a imagem, e o contraste tem que funcionar no galpão.

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100; /* acima do SlideOverPanel (1000), abaixo do toast */
  background: #0b0c0f;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

export const Topo = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

export const TopoTexto = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong { font-size: 14px; font-weight: 700; }
  span {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.62);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Palco = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  /* O pan/pinça é nosso: sem isto o navegador rouba o gesto e o usuário não
     consegue arrastar a foto ampliada nem cortar com o dedo. */
  touch-action: none;
  cursor: ${({ $arrastavel }) => ($arrastavel ? "grab" : "default")};
  &:active { cursor: ${({ $arrastavel }) => ($arrastavel ? "grabbing" : "default")}; }
`;

/**
 * A foto ocupa o palco INTEIRO e o `object-fit: scale-down` letterboxa
 * dentro dela. Parece rebuscado perto de um `max-height: 100%`, mas é o que
 * impede o rodapé da foto de sair cortado.
 *
 * `scale-down` e não `contain` porque `contain` AMPLIA foto pequena, e o
 * comportamento anterior (por `max-height`) não ampliava — trocar isso
 * deixaria foto de baixa resolução borrada sem ninguém ter pedido.
 *
 * O palco era `display: grid; place-items: center` e a foto se limitava por
 * `max-height: 100%`. Só que a linha implícita do grid é `auto`: era
 * dimensionada PELA PRÓPRIA FOTO — medido no navegador, uma foto 960×1280
 * gerava `grid-template-rows: 1280px` num palco de 779px. O `100%` passava
 * a valer contra 1280px (referência circular), a restrição nunca prendia, e
 * a foto transbordava 501px, decapitada pelo `overflow: hidden`.
 *
 * Dimensionando por `inset: 0` não há percentual a resolver: a caixa da
 * imagem é a do palco, por definição. Em troca, `getBoundingClientRect()`
 * passa a devolver o palco e não a foto — por isso `medirArte` calcula a
 * caixa real a partir de `naturalWidth/naturalHeight`. O corte depende dela.
 */
export const Arte = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: scale-down;
  user-select: none;
  -webkit-user-drag: none;
  transform-origin: center center;
  transition: transform 120ms ease-out;
`;

// ---------- corte ----------

// Posicionada por style inline sobre a caixa REAL da imagem — não sobre o
// palco. Como a imagem usa object-fit scale-down, o palco tem tarja preta em
// volta; medir o corte contra ele recortava outra região da foto.
export const CorteCamada = styled.div`
  position: absolute;
  cursor: crosshair;
  touch-action: none;
`;

export const CorteJanela = styled.div`
  position: absolute;
  border: 2px solid #fff;
  /* Escurece TUDO fora da janela com uma sombra gigante — mais simples e mais
     leve que 4 divs de máscara, e acompanha o retângulo de graça. */
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.62);
  cursor: move;
`;

export const CorteAlca = styled.div`
  position: absolute;
  width: 34px;
  height: 34px;
  background: transparent;

  &::after {
    content: "";
    position: absolute;
    width: 14px;
    height: 14px;
    border: 3px solid #fff;
    border-radius: 2px;
  }
  &[data-canto="nw"] { top: -17px; left: -17px; cursor: nwse-resize; }
  &[data-canto="nw"]::after { top: 10px; left: 10px; border-right: none; border-bottom: none; }
  &[data-canto="ne"] { top: -17px; right: -17px; cursor: nesw-resize; }
  &[data-canto="ne"]::after { top: 10px; right: 10px; border-left: none; border-bottom: none; }
  &[data-canto="sw"] { bottom: -17px; left: -17px; cursor: nesw-resize; }
  &[data-canto="sw"]::after { bottom: 10px; left: 10px; border-right: none; border-top: none; }
  &[data-canto="se"] { bottom: -17px; right: -17px; cursor: nwse-resize; }
  &[data-canto="se"]::after { bottom: 10px; right: 10px; border-left: none; border-top: none; }
`;

// ---------- barra de ações ----------

export const Barra = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
`;

// 48px: a tela é usada de luva, em pé, no chão de fábrica.
export const AcaoBtn = styled.button`
  min-width: 48px;
  min-height: 48px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 12px;
  background: ${({ $destaque }) =>
    $destaque ? "#fff" : "rgba(255, 255, 255, 0.12)"};
  color: ${({ $destaque }) => ($destaque ? "#16181e" : "#fff")};
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.38; cursor: not-allowed; }
`;

export const Aviso = styled.div`
  flex: none;
  padding: 8px 14px;
  text-align: center;
  font-size: 13px;
  color: #ffd9a8;
  background: rgba(255, 170, 60, 0.12);
`;
